import { useQuery } from '@tanstack/react-query';
import { TypesenseSearchService } from '@/lib/search/typesense/SearchService';
import { Sequence } from '@/types/sequence';
import { SequenceTransformService, TypesenseHit } from '@/lib/search/SequenceTransformService';
import { SEARCH, PAGINATION } from '@/constants/app.constants';

interface UseTypesenseSequenceFilterParams {
  query?: string;
  filters?: {
    software?: string[];
    holidays?: string[];
    genres?: string[];
    genreSlugs?: string[];
    artists?: string[];
    minPrice?: number;
    maxPrice?: number;
    featured?: boolean;
    isFree?: boolean;
    fileFormats?: string[];
    albums?: string[];
  };
  sort?: string;
  page?: number;
  per_page?: number;
}

export const useTypesenseSequenceFilter = (params: UseTypesenseSequenceFilterParams = {}) => {
  const {
    query = '',
    filters = {},
    sort = SEARCH.DEFAULT_SORT,
    page = 1,
    per_page = PAGINATION.DEFAULT_PAGE_SIZE
  } = params;

  // Query sequences using Typesense directly
  const { data: searchResults, isLoading, error } = useQuery({
    queryKey: ['typesense-sequences', query, filters, sort, page, per_page],
    queryFn: async () => {
      const searchParams: any = {
        query: query || '*',
        filters: filters,
        page,
        per_page,
        // Request all facets needed by the UI. Use schema names to avoid duplicates.
        facet_by: ['software_type', 'holiday_type', 'genre_name', 'artist_name', 'album', 'file_type', 'price'],
        include_facet_counts: true
      };

      if (sort === 'upvotes') {
        searchParams.upvoteSort = true;
      } else if (sort === 'trending') {
        searchParams.trendingSort = true;
      } else if (sort) {
        searchParams.sort_by = sort;
      }

      return await TypesenseSearchService.searchSequences(searchParams);
    },
    staleTime: 30000, // Cache for 30 seconds
    refetchOnWindowFocus: false,
  });

  // Transform Typesense results using centralized service
  const sequences: Sequence[] = SequenceTransformService.transformHitsToSequences(
    (searchResults?.hits || []) as TypesenseHit[]
  );

  // Extract facets for dynamic filtering options
  const facets = searchResults?.facet_counts ? transformFacets(searchResults.facet_counts) : {};
  
  // Get total results and search time
  const totalResults = searchResults?.found || 0;
  const searchTime = searchResults?.search_time_ms || 0;
  
  // Check health status
  const health = { healthy: !error };

  return {
    sequences,
    isLoading,
    error: error?.message,
    facets,
    totalResults,
    searchTime,
    health,
    // Helper functions for UI
    hasResults: sequences.length > 0,
    isEmpty: !isLoading && sequences.length === 0,
    isHealthy: !error,
  };
};

// Helper function to transform facet counts
function transformFacets(facetCounts: any[]): any {
  const facets: any = {};
  
  facetCounts.forEach((facet) => {
    // Standard facet transformation
    facets[facet.field_name] = {
      field_name: facet.field_name,
      counts: facet.counts.map((count: any) => ({
        value: count.value,
        count: count.count,
        highlighted: count.highlighted
      })),
      stats: facet.stats
    };
    
    // Create aliases for UI compatibility
    if (facet.field_name === 'software' || facet.field_name === 'software_type') {
      facets['software_type'] = facets[facet.field_name];
    }
    // Map genre_name to 'genres' for UI compatibility
    if (facet.field_name === 'genre_name') {
      facets['genres'] = facets[facet.field_name];
    }
    // Normalize artist facet naming so UI can reference a stable key
    if (facet.field_name === 'artist_name' || facet.field_name === 'artist') {
      facets['artist'] = facets[facet.field_name];
      facets['artist_name'] = facets[facet.field_name];
    }
    if (facet.field_name === 'file_format' || facet.field_name === 'file_type') {
      facets['file_type'] = facets[facet.field_name];
    }
    // Ensure holiday facet is consistently available
    if (facet.field_name === 'holiday' || facet.field_name === 'holiday_type') {
      facets['holiday_type'] = facets[facet.field_name];
    }
  });
  
  return facets;
} 